import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import {
  addPromotionCode,
  deletePromotionCode,
  editPromotionCode,
  fetchCustomerList,
  fetchCustomerShopPermission,
  fetchCustomerToken,
  fetchPromotionCodeOverview,
} from '../api/shop-admin.api'
import { AdminCustomerListModel, AdminCustomerLoginResponse, AdminShopPermissionModel, PromotionCodeOverviewResponse } from '../models'
import { useShopFetchWithResult } from './use-shop-fetch'
import { useContext, useState } from 'react'
import { AuthShopFetchError } from '../types'
import useShopApiError from './use-shop-errors'
import { useTypedSelector } from 'src/State/RootReducer'
import { AuthContext } from '@e3dc-react/shell/Libs/authentication'

interface UseShopAdminHook {
  checkIsShopUserAdmin: () => boolean
  getCustomerList: (filter?: string) => Promise<AdminCustomerListModel[] | null>
  fetchShopAdminAccess: () => Promise<AdminShopPermissionModel | null>
  getCustomerToken: (customerId: number) => Promise<AdminCustomerLoginResponse | null>
  getPromotionCodeOverview: () => Promise<PromotionCodeOverviewResponse | null>
  createPromotionCode: (amount: number, note?: string) => Promise<PromotionCodeOverviewResponse | null>
  removePromotionCode: (promoCodeId: string) => Promise<PromotionCodeOverviewResponse | null>
  updatePromotionCode: (promoCodeId: string, amount: number, note?: string | null) => Promise<PromotionCodeOverviewResponse | null>
  isLoading: boolean
}

/*
 * This hook holds all the endpoints that we use as an Admin to get Data or Login to another customer.
 */
const useShopAdmin = (): UseShopAdminHook => {
  const authFetch = useAuthFetchWithResponse()
  const shopFetch = useShopFetchWithResult()
  const { shopApiErrorSnackbar } = useShopApiError()
  const { authToken } = useContext(AuthContext)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const isShopUser = useTypedSelector(state => state.shop.contextToken)
  const hasAccess = useTypedSelector(state => state.shop.shopAccess)

  const checkIsShopUserAdmin = (): boolean => {
    if (!hasAccess) {
      return false
    }
    if (isShopUser && hasAccess) {
      return false
    }
    return true
  }

  const getCustomerList = async (filter?: string): Promise<AdminCustomerListModel[] | null> => {
    try {
      const response = await fetchCustomerList(authFetch, filter)
      return response.value
    } catch (error) {
      return null
    }
  }

  const fetchShopAdminAccess = async (): Promise<AdminShopPermissionModel | null> => {
    try {
      if (authToken) {
        const response = await fetchCustomerShopPermission(authToken)
        return response.value
      }
      return null
    } catch (error) {
      console.error(error)
      return null
    }
  }

  const getCustomerToken = async (customerId: number): Promise<AdminCustomerLoginResponse | null> => {
    try {
      if (authToken) {
        const response = await fetchCustomerToken(authToken, customerId, shopFetch)
        return response.value
      }
      return null
    } catch (error) {
      return null
    }
  }

  const getPromotionCodeOverview = async (): Promise<PromotionCodeOverviewResponse | null> => {
    try {
      setIsLoading(true)
      const response = await fetchPromotionCodeOverview(shopFetch)
      return response.value
    } catch (error) {
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const createPromotionCode = async (amount: number, note?: string): Promise<PromotionCodeOverviewResponse | null> => {
    try {
      setIsLoading(true)
      const response = await addPromotionCode(amount, shopFetch, note)
      if (response.hasErrors && response.error) {
        const error = response.error as AuthShopFetchError
        if (error.response?.data) {
          shopApiErrorSnackbar(error.response.data)
        }
        return null
      }
      return response.value
    } catch (error) {
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const removePromotionCode = async (promoCodeId: string): Promise<PromotionCodeOverviewResponse | null> => {
    try {
      setIsLoading(true)
      const response = await deletePromotionCode(promoCodeId, shopFetch)
      if (response.hasErrors && response.error) {
        const error = response.error as AuthShopFetchError
        if (error.response?.data) {
          shopApiErrorSnackbar(error.response.data)
        }
        return null
      }
      return response.value
    } catch (error) {
      return null
    } finally {
      setIsLoading(false)
    }
  }
  const updatePromotionCode = async (promoCodeId: string, amount: number, note?: string | null): Promise<PromotionCodeOverviewResponse | null> => {
    try {
      setIsLoading(true)
      const response = await editPromotionCode(promoCodeId, amount, shopFetch, note)
      if (response.hasErrors && response.error) {
        const error = response.error as AuthShopFetchError
        if (error.response?.data) {
          shopApiErrorSnackbar(error.response.data)
        }
        return null
      }
      return response.value
    } catch (error) {
      return null
    } finally {
      setIsLoading(false)
    }
  }
  return {
    checkIsShopUserAdmin,
    getCustomerList,
    getCustomerToken,
    fetchShopAdminAccess,
    getPromotionCodeOverview,
    createPromotionCode,
    removePromotionCode,
    updatePromotionCode,
    isLoading,
  }
}

export default useShopAdmin
